import { useContext, useRef, useState } from 'react';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { SelectionAlwaysOnDisplay } from '@lexical/react/LexicalSelectionAlwaysOnDisplay';
import ForwardRefContext from 'src/contexts/ForwardRefContext';
import { useEditorState, useOutsideClick } from 'src/hooks';
import { ArtifactType } from 'src/types';
import { CodeEditorToolbar } from './components/CodeEditorToolbar';
import { DocumentEditorToolbar } from './components/DocumentEditorToolbar';
import { EditorContainer } from './components/EditorContainer';
import { EditorHeader } from './components/EditorHeader';
import { PromptPanel } from './components/PromptPanel';
import { DocumentFloatingElementPlugin } from './plugins/DocumentFloatingElementPlugin';
import { CodeEditorContainer } from './components/CodeEditorContainer';
import {
  AutoLinkPlugin,
  FloatingLinkEditorPlugin,
  LinkPlugin,
} from './plugins';
import styles from './Editor.module.scss';
import './editor.scss';
import { DebouncedAutoSavePlugin } from './plugins/DebouncedAutoSavePlugin';
import { useDocumentAutoSave } from 'src/hooks/editorHooks/useDocumentAutoSave';
import { TRANSFORMERS_LIST } from './constants/transformers';
import { TableRowControlsOverlay } from './components/TableRowControlsOverlay/TableRowControlsOverlay';
import { useChatHistory } from 'src/hooks';

export const Editor = () => {
  const { isNinjaRetired } = useChatHistory();
  const containerRef = useRef<HTMLDivElement>(null);
  const { floatingElementPluginRef, editorFloatingTooltipRef } =
    useContext(ForwardRefContext);

  const { editorType } = useEditorState();

  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);

  const { onDocumentAutoSave } = useDocumentAutoSave();

  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null);

  const handleFloatingElementClose = () => {
    if (floatingElementPluginRef?.current?.isOpen) {
      floatingElementPluginRef?.current?.onElementClose();
    }
  };

  const onRef = (floatingAnchorElem: HTMLDivElement) => {
    if (floatingAnchorElem !== null) {
      setFloatingAnchorElem(floatingAnchorElem);
    }
  };

  useOutsideClick(
    containerRef,
    handleFloatingElementClose,
    editorFloatingTooltipRef,
  );

  const handleInsertLinkClick = () => {
    setIsLinkEditMode((prev) => !prev);
    handleFloatingElementClose();
  };

  if (!editorType) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className={styles.mainPanel}>
        <EditorHeader />

        {editorType === ArtifactType.TEXT ? (
          <DocumentEditorToolbar
            onInsertLinkClick={handleInsertLinkClick}
            isLinkEditMode={isLinkEditMode}
          />
        ) : (
          <CodeEditorToolbar />
        )}

        <div className={styles.container} ref={containerRef}>
          {editorType === ArtifactType.TEXT ? (
            // TODO(olha): WIP Editor rename to doc
            <EditorContainer onRef={onRef} />
          ) : (
            <CodeEditorContainer />
          )}

          {!isNinjaRetired && editorType === ArtifactType.TEXT && (
            <DocumentFloatingElementPlugin />
          )}

          <SelectionAlwaysOnDisplay />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <ListPlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS_LIST} />

          <AutoLinkPlugin />
          <LinkPlugin />
          <TablePlugin />
          <TableRowControlsOverlay />
          {floatingAnchorElem && (
            <FloatingLinkEditorPlugin
              anchorElem={floatingAnchorElem}
              isLinkEditMode={isLinkEditMode}
              setIsLinkEditMode={setIsLinkEditMode}
            />
          )}

          {!isNinjaRetired && (
            <DebouncedAutoSavePlugin onSave={onDocumentAutoSave} />
          )}
        </div>
      </div>

      <PromptPanel />
    </div>
  );
};
